import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from './authStore';
import { apiPost } from './api';

/**
 * F-Step 104 — shared workspace-switch primitive.
 *
 * Used by:
 *   - The header `<WorkspaceSwitcher>` dropdown (explicit user click).
 *   - `useNotificationStream` click handler (auto-switch before navigating to a notification's
 *     target tenant so the target's list queries render the right rows).
 *
 * Contract:
 *   1. If `targetTenantId` is null/undefined OR equal to `activeTenantId`, this is a no-op.
 *   2. Otherwise: hit `POST /auth/session/switch-workspace {tenantId}` — the server verifies
 *      the target is one of the user's verified Memberships (Trap 1 defense) and mints a new
 *      access token with the new `activeTenantId` claim.
 *   3. Update the in-memory access token + `activeTenantId` in the auth store.
 *   4. **Trap 2 defense — `queryClient.clear()` before returning.** Every cached list from the
 *      old workspace is flushed. On the next render, TanStack Query refetches everything from
 *      scratch, scoped to the new tenant. Without this, cached data from tenant A renders
 *      briefly in tenant B's view before the refetch — a real leak.
 */

interface SwitchWorkspaceResponse {
  accessToken: string;
  activeTenantId: string;
}

export function useSwitchIfNeeded() {
  const qc = useQueryClient();
  const activeTenantId = useAuthStore((s) => s.activeTenantId);
  const updateAccessToken = useAuthStore((s) => s.updateAccessToken);
  const setActiveTenantId = useAuthStore((s) => s.setActiveTenantId);

  return useCallback(
    async (targetTenantId: string | null | undefined): Promise<boolean> => {
      if (!targetTenantId) return false;
      if (targetTenantId === activeTenantId) return false;
      const res = await apiPost<SwitchWorkspaceResponse>('/auth/session/switch-workspace', {
        tenantId: targetTenantId,
      });
      if (!res?.accessToken || !res?.activeTenantId) {
        throw new Error('switch-workspace: server returned no token');
      }
      updateAccessToken(res.accessToken);
      setActiveTenantId(res.activeTenantId);
      qc.clear();
      return true;
    },
    [qc, activeTenantId, updateAccessToken, setActiveTenantId],
  );
}
